import React, { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useFlags } from '@equipmentshare/feature-flags'
import {
	EuiButton,
	EuiButtonEmpty,
	EuiCallOut,
	EuiDatePicker,
	EuiFieldText,
	EuiFlexGroup,
	EuiFlexItem,
	EuiFormRow,
	EuiHorizontalRule,
	EuiLoadingSpinner,
	EuiModal,
	EuiModalBody,
	EuiModalFooter,
	EuiModalHeader,
	EuiModalHeaderTitle,
	EuiSelect,
	EuiSpacer,
	EuiText,
	EuiTitle,
} from '@equipmentshare/ds2'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { type GridColDef } from '@mui/x-data-grid-premium'
import { euiIconTypeMap } from '../../../components/icons'
import DataGrid from '../../components/DataGrid'
import { FulfillmentType, useFulfillingInventoryStorePartsQuery } from '../../../graphql'
import { LineItem } from '../../../types/demandRequest'
import useBranchQuery from '../../../hooks/useBranchQuery'
import PhoneInput from '../../../components/PhoneInput'
import SubstitutePartPicker, { SubstitutePartOption } from './SubstitutePartPicker'
import NotesModal from './NotesModal'
import useDistributionCenterOptions from '../hooks/useDistributionCenterOptions'
import { getFulfillmentOrderNeededByDate, getMinFulfillmentOrderNeededByDate, filterWeekends } from '../utils/dateValidation'
import {
	InventoryItemMasterStatus,
	inventoryItemMasterDiagnostic,
	inventoryItemMasterStatus,
} from '../services/inventoryItemMaster'

const CheckIcon = euiIconTypeMap.check
const AlertIcon = euiIconTypeMap.alert
const TrashIcon = euiIconTypeMap.trash
const DocumentEditIcon = euiIconTypeMap.documentEdit

// Rows whose item lacks an Inventory Item Master at the fulfilling location are
// tinted; the order cannot be created until they are substituted or removed.
const MISSING_ITEM_MASTER_ROW_CLASS = 'CreateFulfillmentOrderModal-missingItemMasterRow'

export type ItemRowState = {
	lineItem: LineItem,
	quantity: number,
	substitute?: SubstitutePartOption,
	// Seeded from lineItem.fulfillerNotes; only rendered/editable for users with
	// CAN_EDIT_DEMAND_REQUEST_BY_STORE_ID (canEditFulfillerNotes below).
	fulfillerNotes: string,
}

export type ShipToFields = {
	name: string,
	companyName: string,
	line1: string,
	line2: string,
	city: string,
	state: string,
	postalCode: string,
	country: string,
	contactPhone: string,
}

export type ShipToFieldName = keyof ShipToFields

// The fulfilling DC as the form holds it: enough to submit the order (id) and to
// evaluate/explain the Inventory Item Master gate (name, warehouse status).
export type FulfillingInventory = {
	id: string,
	name: string,
	isWarehouse: boolean,
}

export type CreateFulfillmentOrderModalSubmitParams = {
	items: ItemRowState[],
	// The DC that will fulfill this order, as chosen in the form. Defaults to the
	// demand request's own fulfilling inventory but is not bound to it — picking
	// it here is how an order gets created at a mini-DC, and how a requester sets
	// up a Will-Call collection.
	fulfillingInventoryId: string,
	shipTo: ShipToFields,
	overriddenShipToFields: ShipToFieldName[],
	// Whether the ship-to address and contact phone should be submitted. The form
	// always collects (and may prefill) them, but MTT loadouts only send them when
	// the costentoryMttAddressForm flag is on. Mirrors the modal's showShipTo so
	// the hook does not have to re-derive the flag.
	includeShipTo: boolean,
	fulfillmentType: FulfillmentType,
	// MTT loadout details — only populated when fulfillmentType is MTT_LOADOUT.
	mttId: string,
	mttQuadrant: string,
	// Date the order is needed by, serialized as 'YYYY-MM-DD' (or undefined if
	// the field is empty). The mutation input expects a DATE, not a datetime.
	dateNeededBy?: string,
	// Only rows whose fulfillerNotes text actually changed from the line item's
	// original value — the hook fires one UpdateDemandRequestLineItemFulfillerNotes
	// call per entry, rather than for every row regardless of edits.
	fulfillerNotesUpdates: { lineItemId: string, fulfillerNotes: string }[],
}

type CreateFulfillmentOrderModalProps = {
	isOpen: boolean,
	onClose: () => void,
	selectedLineItems: LineItem[],
	// The request's requesting location: which DCs it may be fulfilled from
	// depends on whether a mini-DC serves it (see useDistributionCenterOptions).
	requestingInventoryId?: string | null,
	requestingInventoryBranchId?: string | null,
	// An alternate delivery address entered on the Stock Request. When present it
	// prefills the ship-to instead of the branch's Fleet address - that is the
	// whole point of the requester having supplied one.
	deliveryAddress?: {
		line1?: string | null,
		line2?: string | null,
		city?: string | null,
		state?: string | null,
		postalCode?: string | null,
	} | null,
	// When the requesting inventory's branch is a Mobile Tool Trailer, the form
	// defaults the fulfillment type to MTT loadout (the user can still change it).
	requestingInventoryIsMtt?: boolean | null,
	defaultContactPhone?: string | null,
	// Defaults for the shipping recipient, derived from the demand request's
	// requesting user (and that user's company) — not the logged-in fulfiller.
	defaultRecipientName?: string | null,
	defaultCompanyName?: string | null,
	// The demand request's owning inventory (its fulfilling inventory, or the main
	// DC when a routed stock request has none), used as the *default* for the
	// form's fulfilling-DC picker when it is one of the offered DCs. The user can
	// pick another offered DC.
	defaultFulfillingInventoryId: string,
	// Name and warehouse status of the default fulfilling inventory. Together with
	// each product's active-item-master flag and bin location they form the full
	// Inventory Item Master gate the modal evaluates per row — but only while the
	// default DC is still selected (see itemMasterStatuses below).
	defaultFulfillingInventoryName: string,
	defaultFulfillingInventoryIsWarehouse: boolean,
	isSubmitting: boolean,
	onSubmit: ( params: CreateFulfillmentOrderModalSubmitParams ) => Promise<void> | void,
	// Whether this user (CAN_EDIT_DEMAND_REQUEST_BY_STORE_ID) can see and edit the
	// per-line fulfiller notes column. Hidden entirely for everyone else — the
	// update mutation is gated server-side by the same permission.
	canEditFulfillerNotes: boolean,
}

// US ISO 3166 alpha-2 country code. Fulfillment orders are US-only, so the
// country is hard-coded rather than entered by the user.
const DEFAULT_COUNTRY_CODE = 'US'

const EMPTY_SHIP_TO: ShipToFields = {
	name: '',
	companyName: '',
	line1: '',
	line2: '',
	city: '',
	state: '',
	postalCode: '',
	country: DEFAULT_COUNTRY_CODE,
	contactPhone: '',
}

type ShipToValidation = {
	recipientMissing: boolean,
	line1Missing: boolean,
	cityMissing: boolean,
	stateMissing: boolean,
	postalCodeMissing: boolean,
	isInvalid: boolean,
}

const VALID_SHIP_TO: ShipToValidation = {
	recipientMissing: false,
	line1Missing: false,
	cityMissing: false,
	stateMissing: false,
	postalCodeMissing: false,
	isInvalid: false,
}

// REST branch responses are loosely typed and can return numeric values despite
// the string-typed shape, so coerce to a string before they enter ShipToFields
// and the string operations below (.trim()) run on them. zip_code in particular
// is already normalized to a padded string by the RestLink typePatcher (see
// config/apollo-client.ts), so the leading zero is intact by the time it lands
// here — this is just the general numeric-to-string safety net.
function toShipToString( value: unknown ): string {
	return value == null ? '' : String( value )
}

// A shipping address is "provided" when any recipient or address field is set.
// Country is excluded — it always defaults to US and must not, on its own, make
// the form look like it has an address.
function hasShipToAddress( shipTo: ShipToFields ): boolean {
	return !!(
		shipTo.name.trim() ||
		shipTo.companyName.trim() ||
		shipTo.line1.trim() ||
		shipTo.line2.trim() ||
		shipTo.city.trim() ||
		shipTo.state.trim() ||
		shipTo.postalCode.trim()
	)
}

// When a shipping address is provided it must be complete enough to ship: a
// recipient (name or company) plus street line 1, city, state, and postal code.
// line_2 and country are optional. Mirrors the inventory-api rule so the client
// blocks before the server rejects.
function validateShipTo( shipTo: ShipToFields ): ShipToValidation {
	if ( !hasShipToAddress( shipTo ) ) {
		return VALID_SHIP_TO
	}
	const recipientMissing = !shipTo.name.trim() && !shipTo.companyName.trim()
	const line1Missing = !shipTo.line1.trim()
	const cityMissing = !shipTo.city.trim()
	const stateMissing = !shipTo.state.trim()
	const postalCodeMissing = !shipTo.postalCode.trim()
	return {
		recipientMissing,
		line1Missing,
		cityMissing,
		stateMissing,
		postalCodeMissing,
		isInvalid: recipientMissing || line1Missing || cityMissing || stateMissing || postalCodeMissing,
	}
}

// E.164 phone number validation regex (for US numbers: +1 followed by 10 digits)
const E164_PHONE_REGEX = /^\+1\d{10}$/

// Contact phone is required — Numina requires a phone number on fulfillment orders.
function validateContactPhone( phone: string ): { isInvalid: boolean, error?: string } {
	if ( !phone.trim() ) {
		return { isInvalid: true, error: 'Contact phone is required' }
	}
	if ( !E164_PHONE_REGEX.test( phone ) ) {
		return { isInvalid: true, error: 'Please enter a valid 10-digit US phone number' }
	}
	return { isInvalid: false }
}

const FULFILLMENT_TYPE_OPTIONS = [
	{ value: FulfillmentType.InventoryTransfer, text: 'Inventory transfer' },
	{ value: FulfillmentType.DirectShipment, text: 'Direct shipment' },
	{ value: FulfillmentType.MttLoadout, text: 'MTT loadout' },
]

// Earliest neededBy among the selected line items, as 'YYYY-MM-DD'. Line items
// with no neededBy are ignored; if none have a date, returns ''. neededBy comes
// back from the demand-request query as a DATE scalar (a 'YYYY-MM-DD' string).
function earliestNeededBy( lineItems: LineItem[] ): string {
	const dates = lineItems
		.map( ( li ) => li.neededBy )
		.filter( ( d ): d is NonNullable<typeof d> => d != null )
		.map( ( d ) => moment( d ) )
		.filter( ( m ) => m.isValid() )
	if ( dates.length === 0 ) {
		return ''
	}
	return moment.min( dates ).format( 'YYYY-MM-DD' )
}

// Default "Date needed by" for the fulfillment order, populated from the
// Stock Request's own Needs By date and corrected forward per the 3pm CT
// cutoff rule if that date is no longer achievable. The user can still
// change it afterward — this only affects the default value.
function defaultFulfillmentOrderNeededBy( lineItems: LineItem[] ): moment.Moment | null {
	const stockRequestNeededBy = earliestNeededBy( lineItems )
	const adjusted = getFulfillmentOrderNeededByDate( stockRequestNeededBy )
	return adjusted ? moment( adjusted ) : null
}

function initialItems( lineItems: LineItem[] ): ItemRowState[] {
	return lineItems.map( ( lineItem ) => ( {
		lineItem,
		quantity: lineItem.quantityRequested,
		fulfillerNotes: lineItem.fulfillerNotes ?? '',
	} ) )
}

/** Bin location and availability for one product at the DC that will fill it. */
export type RowStock = {
	binLocation: string | null | undefined,
	availableQuantity: number | null | undefined,
}

// The fulfillable product for a row is its substitute when one is chosen,
// otherwise the originally requested product. A substitute can carry an item
// master the original lacks (the substitute picker only surfaces parts with an
// active item master), which is exactly the case this modal must allow.
//
// `hasActiveItemMaster` is a property of the product alone — inventory-api
// resolves it per product id, not per location — so it holds whichever DC fills
// the order. Bin and availability do not, which is what `stock` supplies.
function rowFulfillsWith( row: ItemRowState, stock: RowStock ): {
	hasActiveItemMaster: boolean,
	binLocation: string | null | undefined,
	label: string,
} {
	if ( row.substitute ) {
		return {
			hasActiveItemMaster: row.substitute.part.hasActiveItemMaster,
			binLocation: row.substitute.location,
			label: row.substitute.part.partNumber ?? row.substitute.part.partClass.description,
		}
	}
	return {
		hasActiveItemMaster: row.lineItem.product.hasActiveItemMaster,
		binLocation: stock.binLocation,
		label: row.lineItem.product.partNumber ?? row.lineItem.product.manufacturerNumber ?? row.lineItem.product.name,
	}
}

// Evaluates the full Inventory Item Master gate for a row's effective product,
// mirroring inventory-api so the modal blocks the same orders the backend would
// reject. Warehouse status is a property of the fulfilling location (shared by
// the original and any substitute, which is searched at that same location).
function rowItemMasterStatus(
	row: ItemRowState,
	locationIsWarehouse: boolean,
	stock: RowStock,
): InventoryItemMasterStatus {
	const { hasActiveItemMaster, binLocation } = rowFulfillsWith( row, stock )
	return inventoryItemMasterStatus( {
		hasActiveItemMaster,
		locationIsWarehouse,
		binLocation,
	} )
}

export default function CreateFulfillmentOrderModal( {
	isOpen,
	onClose,
	selectedLineItems,
	requestingInventoryId,
	requestingInventoryBranchId,
	deliveryAddress,
	requestingInventoryIsMtt,
	defaultContactPhone,
	defaultRecipientName,
	defaultCompanyName,
	defaultFulfillingInventoryId,
	defaultFulfillingInventoryName,
	defaultFulfillingInventoryIsWarehouse,
	isSubmitting,
	onSubmit,
	canEditFulfillerNotes,
}: CreateFulfillmentOrderModalProps ) {
	// MTT branches default the fulfillment type to an MTT loadout; everything else
	// defaults to an inventory transfer. This is only a default — the user can still
	// pick a different type from the dropdown.
	const defaultFulfillmentType = requestingInventoryIsMtt
		? FulfillmentType.MttLoadout
		: FulfillmentType.InventoryTransfer

	const [ items, setItems ] = useState<ItemRowState[]>( () => initialItems( selectedLineItems ) )
	// The fulfilling DC as chosen, defaulted to the demand request's own.
	const defaultFulfillingInventory: FulfillingInventory = {
		id: defaultFulfillingInventoryId,
		name: defaultFulfillingInventoryName,
		isWarehouse: defaultFulfillingInventoryIsWarehouse,
	}
	const [ chosenFulfillingInventory, setChosenFulfillingInventory ] = useState<FulfillingInventory>( defaultFulfillingInventory )
	// The DCs this request may be fulfilled from: its requesting location's serving
	// mini-DCs plus the main DC, or only the main DC when no mini-DC serves it.
	// Asked only while open, so the details page does not look it up unprompted.
	const distributionCenters = useDistributionCenterOptions( isOpen ? requestingInventoryId : null )
	// The DC that fills the order: the choice while it is offered, else the main DC.
	// Held at the choice while the options load, so a request already at a mini-DC
	// does not flash to the main DC (and fetch its stock) in the meantime.
	const fulfillingInventory: FulfillingInventory = distributionCenters.loading
		? chosenFulfillingInventory
		: distributionCenters.options.find( ( dc ) => dc.id === chosenFulfillingInventory.id ) ??
			distributionCenters.mainDistributionCenter ??
			chosenFulfillingInventory
	// Mirrors the request-details page's fulfiller-notes pattern: a single icon
	// opens a NotesModal for whichever row's lineItemId is set here.
	const [ notesModalLineItemId, setNotesModalLineItemId ] = useState<string | null>( null )
	const [ shipTo, setShipTo ] = useState<ShipToFields>( () => ( {
		...EMPTY_SHIP_TO,
		name: defaultRecipientName ?? '',
		companyName: defaultCompanyName ?? '',
		contactPhone: defaultContactPhone ?? '',
	} ) )
	const [ hasPrefilledAddress, setHasPrefilledAddress ] = useState( false )
	const [ touchedShipToFields, setTouchedShipToFields ] = useState<Set<ShipToFieldName>>( () => new Set() )
	const [ fulfillmentType, setFulfillmentType ] = useState<FulfillmentType>( defaultFulfillmentType )
	const [ mttId, setMttId ] = useState( '' )
	const [ mttQuadrant, setMttQuadrant ] = useState( '' )
	// Editable date-needed-by, defaulted to the Stock Request's Needs By date
	// (corrected forward per the 3pm CT cutoff rule if it's no longer
	// achievable). Stored as a moment for EuiDatePicker; serialized on submit.
	const [ dateNeededBy, setDateNeededBy ] = useState<moment.Moment | null>(
		() => defaultFulfillmentOrderNeededBy( selectedLineItems ),
	)

	const flags = useFlags()
	const mttAddressFormEnabled = Boolean( flags.costentoryMttAddressForm )

	const { fetchBranch, branch, loading: branchLoading } = useBranchQuery()

	useEffect( () => {
		if ( isOpen ) {
			setItems( initialItems( selectedLineItems ) )
			setChosenFulfillingInventory( {
				id: defaultFulfillingInventoryId,
				name: defaultFulfillingInventoryName,
				isWarehouse: defaultFulfillingInventoryIsWarehouse,
			} )
			setShipTo( {
				...EMPTY_SHIP_TO,
				name: defaultRecipientName ?? '',
				companyName: defaultCompanyName ?? '',
				contactPhone: defaultContactPhone ?? '',
			} )
			setHasPrefilledAddress( false )
			setTouchedShipToFields( new Set() )
			setFulfillmentType( defaultFulfillmentType )
			setMttId( '' )
			setMttQuadrant( '' )
			setNotesModalLineItemId( null )
			setDateNeededBy( defaultFulfillmentOrderNeededBy( selectedLineItems ) )
			if ( requestingInventoryBranchId ) {
				// TODO: replace with new branches query
				fetchBranch( requestingInventoryBranchId )
			}
		}
	}, [ isOpen, selectedLineItems, requestingInventoryBranchId, defaultFulfillmentType, defaultContactPhone, defaultRecipientName, defaultCompanyName, defaultFulfillingInventoryId, defaultFulfillingInventoryName, defaultFulfillingInventoryIsWarehouse, fetchBranch ] )

	useEffect( () => {
		if ( !isOpen || hasPrefilledAddress ) {
			return
		}

		// The requester named somewhere other than the branch to deliver to, so
		// that wins outright. It also needs no branch fetch, so it fills straight
		// away rather than waiting on one.
		if ( deliveryAddress ) {
			setShipTo( ( prev ) => ( {
				...prev,
				line1: prev.line1 || toShipToString( deliveryAddress.line1 ),
				line2: prev.line2 || toShipToString( deliveryAddress.line2 ),
				city: prev.city || toShipToString( deliveryAddress.city ),
				state: prev.state || toShipToString( deliveryAddress.state ),
				postalCode: prev.postalCode || toShipToString( deliveryAddress.postalCode ),
			} ) )
			setHasPrefilledAddress( true )
			return
		}

		if ( !branch?.location ) {
			return
		}
		const location = branch.location
		setShipTo( ( prev ) => ( {
			name: prev.name,
			companyName: prev.companyName,
			line1: prev.line1 || toShipToString( location.street_1 ),
			line2: prev.line2 || toShipToString( location.street_2 ),
			city: prev.city || toShipToString( location.city ),
			state: prev.state || toShipToString( location.state?.abbreviation ),
			postalCode: prev.postalCode || toShipToString( location.zip_code ),
			country: prev.country,
			contactPhone: prev.contactPhone,
		} ) )
		setHasPrefilledAddress( true )
	}, [ isOpen, hasPrefilledAddress, branch, deliveryAddress ] )

	const showMttFields = fulfillmentType === FulfillmentType.MttLoadout
	// The ship-to address and contact phone are no longer required by the mutation
	// for MTT loadouts. Only collect them for MTT loadouts when the address-form
	// flag is enabled; every other fulfillment type still requires them.
	const showShipTo = fulfillmentType !== FulfillmentType.MttLoadout || mttAddressFormEnabled

	const contactPhoneValidation = validateContactPhone( shipTo.contactPhone )
	const shipToValidation = validateShipTo( shipTo )
	const addressRequiredButMissing = showShipTo && !hasShipToAddress( shipTo )
	const recipientMissing = shipToValidation.recipientMissing

	const mttIdMissing = showMttFields && !mttId.trim()
	const mttQuadrantMissing = showMttFields && !mttQuadrant.trim()

	// The per-line availableQuantity and bin location on the demand request are
	// projections at the *request's* fulfilling inventory, so they only describe
	// the order when the request's own DC is the one filling it.
	const isDefaultFulfillingInventory = fulfillingInventory.id === defaultFulfillingInventoryId

	// The store-parts lookup below addresses the DC by numeric id.
	const parsedFulfillingInventoryId = Number.parseInt( fulfillingInventory.id, 10 )
	const fulfillingInventoryIdNumber = Number.isFinite( parsedFulfillingInventoryId )
		? parsedFulfillingInventoryId
		: undefined

	// Bin and availability for the requested products at whichever DC was chosen.
	// Only fetched when that is not the request's own DC, whose numbers already
	// arrived with the line items.
	const requestedPartIds = useMemo(
		() => items.map( ( row ) => Number( row.lineItem.product.id ) ).filter( Number.isFinite ),
		[ items ],
	)
	const { data: chosenDcStockData, loading: chosenDcStockLoading } = useFulfillingInventoryStorePartsQuery( {
		variables: {
			storeIds: fulfillingInventoryIdNumber !== undefined ? [ fulfillingInventoryIdNumber ] : [],
			partIds: requestedPartIds,
		},
		skip: !isOpen ||
			isDefaultFulfillingInventory ||
			fulfillingInventoryIdNumber === undefined ||
			requestedPartIds.length === 0,
		fetchPolicy: 'cache-and-network',
	} )

	// product id → what that product looks like at the chosen DC.
	const chosenDcStockByPartId = useMemo( () => {
		const byPartId = new Map<string, RowStock>()
		chosenDcStockData?.storeParts.forEach( ( storePart ) => {
			byPartId.set( String( storePart.partId ), {
				binLocation: storePart.location,
				availableQuantity: storePart.availableQuantity,
			} )
		} )
		return byPartId
	}, [ chosenDcStockData ] )

	// Where a row's bin and availability come from. A substitute carries its own,
	// already searched at the chosen DC by SubstitutePartPicker. Otherwise it is
	// the request's projection at its own DC, or the lookup above at any other.
	// A product absent from the lookup is not stocked there, which is an answer.
	const stockForRow = useCallback( ( row: ItemRowState ): RowStock => {
		if ( row.substitute ) {
			return {
				binLocation: row.substitute.location,
				availableQuantity: row.substitute.availableQuantity,
			}
		}
		if ( isDefaultFulfillingInventory ) {
			return {
				binLocation: row.lineItem.fulfillingInventoryStorePart?.location,
				availableQuantity: row.lineItem.fulfillingInventoryStorePart?.availableQuantity,
			}
		}
		return chosenDcStockByPartId.get( String( row.lineItem.product.id ) ) ??
			{ binLocation: null, availableQuantity: null }
	}, [ isDefaultFulfillingInventory, chosenDcStockByPartId ] )

	// Full Inventory Item Master gate per row, keyed by line item id. A substitute
	// can flip a row from blocked to fulfillable, so this is recomputed whenever an
	// item or its substitute changes — and now also when the chosen DC's stock
	// arrives, since the bin half of the gate depends on it.
	const itemMasterStatuses = useMemo( () => {
		const statuses = new Map<string, InventoryItemMasterStatus>()
		items.forEach( ( row ) => {
			statuses.set(
				row.lineItem.lineItemId,
				rowItemMasterStatus( row, fulfillingInventory.isWarehouse, stockForRow( row ) ),
			)
		} )
		return statuses
	}, [ items, fulfillingInventory.isWarehouse, stockForRow ] )

	const missingItemMasterCount = useMemo(
		() => Array.from( itemMasterStatuses.values() ).filter( ( status ) => !status.isPresent ).length,
		[ itemMasterStatuses ],
	)

	const isValid = useMemo( () => {
		const shipToValid = !showShipTo || ( hasShipToAddress( shipTo ) && !shipToValidation.isInvalid )
		const phoneValid = !showShipTo || !contactPhoneValidation.isInvalid
		const mttValid = showMttFields ? !!mttId.trim() && !!mttQuadrant.trim() : true
		return items.length > 0 &&
			items.every( ( row ) => row.quantity > 0 ) &&
			!!fulfillingInventory.id &&
			!distributionCenters.loading &&
			missingItemMasterCount === 0 &&
			phoneValid &&
			shipToValid &&
			mttValid
	}, [
		items,
		fulfillingInventory.id,
		distributionCenters.loading,
		missingItemMasterCount,
		contactPhoneValidation.isInvalid,
		shipToValidation.isInvalid,
		showShipTo,
		showMttFields,
		shipTo,
		mttId,
		mttQuadrant,
	] )

	if ( !isOpen ) {
		return null
	}

	const updateItem = ( lineItemId: string, patch: Partial<ItemRowState> ) => {
		setItems( ( prev ) => prev.map( ( row ) => (
			row.lineItem.lineItemId === lineItemId ? { ...row, ...patch } : row
		) ) )
	}

	const removeItem = ( lineItemId: string ) => {
		setItems( ( prev ) => prev.filter( ( row ) => row.lineItem.lineItemId !== lineItemId ) )
	}

	// Substitutes are searched against the fulfilling DC's store parts, so a
	// substitute chosen at one DC is meaningless at another — clear them in the
	// handler that changes the DC rather than reacting to the change in an effect.
	const handleFulfillingInventoryChange = ( id: string ) => {
		const dc = distributionCenters.options.find( ( option ) => option.id === id )
		if ( !dc ) {
			return
		}
		setChosenFulfillingInventory( dc )
		setItems( ( prev ) => prev.map( ( row ) => ( { ...row, substitute: undefined } ) ) )
	}

	const notesModalRow = notesModalLineItemId
		? items.find( ( row ) => row.lineItem.lineItemId === notesModalLineItemId )
		: undefined

	const handleSaveFulfillerNote = ( notes: string ) => {
		if ( notesModalLineItemId ) {
			updateItem( notesModalLineItemId, { fulfillerNotes: notes } )
		}
		setNotesModalLineItemId( null )
	}

	const updateShipTo = ( patch: Partial<ShipToFields> ) => {
		setShipTo( ( prev ) => ( { ...prev, ...patch } ) )
		setTouchedShipToFields( ( prev ) => {
			const next = new Set( prev )
			for ( const key of Object.keys( patch ) as ShipToFieldName[] ) {
				next.add( key )
			}
			return next
		} )
	}

	const handleSubmit = async() => {
		if ( !isValid || isSubmitting ) {
			return
		}
		const fulfillerNotesUpdates = canEditFulfillerNotes
			? items
				.filter( ( row ) => row.fulfillerNotes !== ( row.lineItem.fulfillerNotes ?? '' ) )
				.map( ( row ) => ( { lineItemId: row.lineItem.lineItemId, fulfillerNotes: row.fulfillerNotes } ) )
			: []
		await onSubmit( {
			items,
			fulfillingInventoryId: fulfillingInventory.id,
			shipTo,
			overriddenShipToFields: Array.from( touchedShipToFields ),
			includeShipTo: showShipTo,
			fulfillmentType,
			mttId,
			mttQuadrant,
			dateNeededBy: dateNeededBy ? dateNeededBy.format( 'YYYY-MM-DD' ) : undefined,
			fulfillerNotesUpdates,
		} )
	}

	const handleClose = () => {
		if ( isSubmitting ) {
			return
		}
		onClose()
	}

	const notesColumn: GridColDef<ItemRowState> = {
		field: 'notes',
		headerName: 'Notes',
		width: 70,
		align: 'center',
		headerAlign: 'center',
		sortable: false,
		renderCell: ( { row } ) => {
			const hasNote = !!row.fulfillerNotes.trim()
			return (
				<Tooltip title={hasNote ? 'Edit fulfillment note' : 'Add fulfillment note'}>
					<IconButton
						size="small"
						aria-label={`${hasNote ? 'Edit' : 'Add'} fulfillment note for ${row.lineItem.product.partNumber ?? row.lineItem.lineItemId}`}
						onClick={() => setNotesModalLineItemId( row.lineItem.lineItemId )}
					>
						<DocumentEditIcon fontSize="inherit" />
					</IconButton>
				</Tooltip>
			)
		},
	}

	const itemColumns: GridColDef<ItemRowState>[] = [
		{
			field: 'requestedItem',
			headerName: 'Requested Item',
			width: 280,
			sortable: false,
			renderCell: ( { row } ) => (
				<Stack direction="column" spacing={0.25}>
					<Typography variant="body2">
						{row.lineItem.product.partNumber ?? '\u2014'} - {row.lineItem.product.partClass.description}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{row.lineItem.product.provider.name}
					</Typography>
				</Stack>
			),
		},
		{
			field: 'requestedQty',
			headerName: 'Requested Qty',
			width: 110,
			sortable: false,
			renderCell: ( { row } ) => (
				<Typography variant="body2">{row.lineItem.quantityRequested}</Typography>
			),
		},
		{
			field: 'availableQty',
			headerName: 'Qty Avail.',
			width: 100,
			sortable: false,
			renderCell: ( { row } ) => {
				if ( chosenDcStockLoading ) {
					return <CircularProgress size={16} />
				}
				// Strict null check: 0 must render as "0", only null/undefined renders as "\u2014"
				const quantity = stockForRow( row ).availableQuantity
				if ( quantity === null || quantity === undefined ) {
					return (
						<Tooltip title={`Not stocked at ${fulfillingInventory.name}`}>
							<Typography variant="body2">{'\u2014'}</Typography>
						</Tooltip>
					)
				}
				return <Typography variant="body2">{String( quantity )}</Typography>
			},
		},
		{
			field: 'fulfillQty',
			headerName: 'Fulfill Qty',
			width: 110,
			sortable: false,
			renderCell: ( { row } ) => (
				<TextField
					type="number"
					value={row.quantity}
					slotProps={{ htmlInput: { 'min': 1, 'aria-label': `Fulfill quantity for ${row.lineItem.product.partNumber ?? row.lineItem.lineItemId}` } }}
					onChange={( e ) => {
						const next = Number.parseInt( e.target.value, 10 )
						updateItem( row.lineItem.lineItemId, {
							quantity: Number.isFinite( next ) ? next : 0,
						} )
					}}
				/>
			),
		},
		{
			field: 'substitute',
			headerName: 'Substitute',
			flex: 1,
			minWidth: 220,
			sortable: false,
			renderCell: ( { row } ) => {
				const picker = (
					<SubstitutePartPicker
						fulfillingInventoryId={fulfillingInventory.id}
						selectedOption={row.substitute}
						onChange={( option ) => updateItem( row.lineItem.lineItemId, { substitute: option } )}
						hideLabel
						fullWidth
					/>
				)
				if ( row.lineItem.allowSubstitution ) {
					return picker
				}
				// The requester marked this line as not substitutable \u2014 anyone who can
				// open this form can still substitute it, but the box and helper text
				// below flag it so the override is a deliberate, visible choice.
				return (
					<Stack direction="column" spacing={0.25} sx={{ width: '100%' }}>
						<Box sx={{
							border: 1,
							borderColor: 'formPanel.warningBorder',
							borderRadius: 1,
							bgcolor: 'formPanel.warningBackground',
							p: '2px',
						}}>
							{picker}
						</Box>
						<Typography variant="caption" color="warning.main" fontWeight="bold">
							The requester did not allow substitutions for this item.
						</Typography>
					</Stack>
				)
			},
		},
		...( canEditFulfillerNotes ? [ notesColumn ] : [] ),
		{
			field: 'itemMaster',
			headerName: 'Item Master',
			width: 110,
			align: 'center',
			headerAlign: 'center',
			sortable: false,
			renderCell: ( { row } ) => {
				if ( chosenDcStockLoading ) {
					return <CircularProgress size={16} />
				}
				const status = itemMasterStatuses.get( row.lineItem.lineItemId )
				if ( status?.isPresent ) {
					return (
						<Tooltip title="Has Inventory Item Master">
							<CheckIcon fontSize="small" color="success" />
						</Tooltip>
					)
				}
				const reason = status
					? inventoryItemMasterDiagnostic( status, {
						productLabel: rowFulfillsWith( row, stockForRow( row ) ).label,
						locationName: fulfillingInventory.name,
					} )
					: null
				return (
					<Tooltip title={reason ?? 'Missing Inventory Item Master'}>
						<AlertIcon fontSize="small" color="error" />
					</Tooltip>
				)
			},
		},
		{
			field: 'remove',
			headerName: '',
			width: 48,
			align: 'center',
			headerAlign: 'center',
			sortable: false,
			renderCell: ( { row } ) => (
				<IconButton
					size="small"
					color="error"
					aria-label={`Remove ${row.lineItem.product.partNumber ?? row.lineItem.lineItemId} from this order`}
					onClick={() => removeItem( row.lineItem.lineItemId )}
				>
					<TrashIcon fontSize="inherit" />
				</IconButton>
			),
		},
	]

	return (
		<EuiModal onClose={handleClose} style={{ width: 1200, maxWidth: '90vw' }}>
			<EuiModalHeader>
				<EuiModalHeaderTitle>Create Fulfillment Order</EuiModalHeaderTitle>
			</EuiModalHeader>
			<EuiModalBody>
				<EuiTitle size="xs"><h3>Fulfillment details</h3></EuiTitle>
				<EuiSpacer size="m" />
				<EuiFlexGroup gutterSize="m" wrap responsive={false}>
					<EuiFlexItem style={{ minWidth: 320, maxWidth: 320 }}>
						{/* The fulfilling DC is chosen here, not inherited from the request:
						    a serving mini-DC or the main DC. Locked to the main DC when no
						    mini-DC serves the requesting location. */}
						<EuiFormRow label={<>Fulfilling DC <span className="required">*</span></>} fullWidth>
							<EuiSelect
								fullWidth
								id="fulfilling-inventory"
								options={distributionCenters.options.map( ( dc ) => ( { value: dc.id, text: dc.name } ) )}
								value={fulfillingInventory.id}
								onChange={( e ) => handleFulfillingInventoryChange( e.target.value )}
								disabled={!distributionCenters.servedByMiniDc}
								isLoading={distributionCenters.loading}
								data-testid="fulfilling-inventory-picker"
							/>
						</EuiFormRow>
					</EuiFlexItem>
					<EuiFlexItem style={{ minWidth: 280, maxWidth: 280 }}>
						<EuiFormRow label={<>Fulfillment type <span className="required">*</span></>} fullWidth>
							<EuiSelect
								fullWidth
								options={FULFILLMENT_TYPE_OPTIONS}
								value={fulfillmentType}
								onChange={( e ) => setFulfillmentType( e.target.value as FulfillmentType )}
							/>
						</EuiFormRow>
					</EuiFlexItem>
					<EuiFlexItem grow={false} style={{ minWidth: 220, maxWidth: 220 }}>
						<EuiFormRow label="Date needed by" fullWidth>
							<EuiDatePicker
								selected={dateNeededBy}
								onChange={( date ) => setDateNeededBy( date )}
								placeholder="Select date"
								minDate={getMinFulfillmentOrderNeededByDate()}
								filterDate={filterWeekends}
							/>
						</EuiFormRow>
					</EuiFlexItem>
				</EuiFlexGroup>

				{!isDefaultFulfillingInventory && (
					<>
						<EuiSpacer size="s" />
						<EuiCallOut
							size="s"
							color="primary"
							iconType="iInCircle"
							title={`This order will be fulfilled from ${fulfillingInventory.name}, not ${defaultFulfillingInventoryName}.`}
						/>
					</>
				)}

				{showMttFields && (
					<>
						<EuiSpacer size="m" />
						<EuiFlexGroup gutterSize="m" wrap responsive={false}>
							<EuiFlexItem style={{ minWidth: 280, maxWidth: 280 }}>
								<EuiFormRow
									label={<>MTT ID <span className="required">*</span></>}
									fullWidth
									isInvalid={mttIdMissing}
									error={mttIdMissing ? 'Required' : undefined}
								>
									<EuiFieldText
										fullWidth
										isInvalid={mttIdMissing}
										value={mttId}
										onChange={( e ) => setMttId( e.target.value )}
									/>
								</EuiFormRow>
							</EuiFlexItem>
							<EuiFlexItem style={{ minWidth: 280, maxWidth: 280 }}>
								<EuiFormRow
									label={<>MTT quadrant <span className="required">*</span></>}
									fullWidth
									isInvalid={mttQuadrantMissing}
									error={mttQuadrantMissing ? 'Required' : undefined}
								>
									<EuiFieldText
										fullWidth
										isInvalid={mttQuadrantMissing}
										value={mttQuadrant}
										onChange={( e ) => setMttQuadrant( e.target.value )}
									/>
								</EuiFormRow>
							</EuiFlexItem>
						</EuiFlexGroup>
					</>
				)}

				{showShipTo && (
					<>
						<EuiHorizontalRule />

						<EuiTitle size="xs"><h3>Ship to</h3></EuiTitle>
						{addressRequiredButMissing && (
							<>
								<EuiSpacer size="s" />
								<EuiText size="xs" color="danger">A shipping address is required for this fulfillment type.</EuiText>
							</>
						)}
						<EuiSpacer size="m" />
						<EuiFlexGroup gutterSize="m" wrap responsive={false}>
							<EuiFlexItem style={{ minWidth: 280 }}>
								<EuiFormRow
									label="Recipient name"
									fullWidth
									isInvalid={recipientMissing}
									error={recipientMissing ? 'Enter a recipient name or company name' : undefined}
								>
									<EuiFieldText
										fullWidth
										isInvalid={recipientMissing}
										value={shipTo.name}
										onChange={( e ) => updateShipTo( { name: e.target.value } )}
									/>
								</EuiFormRow>
							</EuiFlexItem>
							<EuiFlexItem style={{ minWidth: 280 }}>
								<EuiFormRow label="Company name" fullWidth isInvalid={recipientMissing}>
									<EuiFieldText
										fullWidth
										isInvalid={recipientMissing}
										value={shipTo.companyName}
										onChange={( e ) => updateShipTo( { companyName: e.target.value } )}
									/>
								</EuiFormRow>
							</EuiFlexItem>
						</EuiFlexGroup>
						<EuiSpacer size="m" />
						{branchLoading && !hasPrefilledAddress && (
							<>
								<EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
									<EuiFlexItem grow={false}><EuiLoadingSpinner size="s" /></EuiFlexItem>
									<EuiFlexItem grow={false}>
										<EuiText size="xs" color="subdued">Loading branch address…</EuiText>
									</EuiFlexItem>
								</EuiFlexGroup>
								<EuiSpacer size="s" />
							</>
						)}
						<EuiFlexGroup gutterSize="m" wrap responsive={false}>
							<EuiFlexItem style={{ minWidth: 280 }}>
								<EuiFormRow
									label="Address line 1"
									fullWidth
									isInvalid={shipToValidation.line1Missing}
									error={shipToValidation.line1Missing ? 'Required' : undefined}
								>
									<EuiFieldText
										fullWidth
										isInvalid={shipToValidation.line1Missing}
										value={shipTo.line1}
										onChange={( e ) => updateShipTo( { line1: e.target.value } )}
									/>
								</EuiFormRow>
							</EuiFlexItem>
							<EuiFlexItem style={{ minWidth: 280 }}>
								<EuiFormRow label="Address line 2" fullWidth>
									<EuiFieldText
										fullWidth
										value={shipTo.line2}
										onChange={( e ) => updateShipTo( { line2: e.target.value } )}
									/>
								</EuiFormRow>
							</EuiFlexItem>
						</EuiFlexGroup>
						<EuiSpacer size="m" />
						<EuiFlexGroup gutterSize="m" wrap responsive={false}>
							<EuiFlexItem style={{ minWidth: 180 }}>
								<EuiFormRow
									label="City"
									fullWidth
									isInvalid={shipToValidation.cityMissing}
									error={shipToValidation.cityMissing ? 'Required' : undefined}
								>
									<EuiFieldText
										fullWidth
										isInvalid={shipToValidation.cityMissing}
										value={shipTo.city}
										onChange={( e ) => updateShipTo( { city: e.target.value } )}
									/>
								</EuiFormRow>
							</EuiFlexItem>
							<EuiFlexItem style={{ minWidth: 120 }}>
								<EuiFormRow
									label="State"
									fullWidth
									isInvalid={shipToValidation.stateMissing}
									error={shipToValidation.stateMissing ? 'Required' : undefined}
								>
									<EuiFieldText
										fullWidth
										isInvalid={shipToValidation.stateMissing}
										value={shipTo.state}
										onChange={( e ) => updateShipTo( { state: e.target.value } )}
									/>
								</EuiFormRow>
							</EuiFlexItem>
							<EuiFlexItem style={{ minWidth: 140 }}>
								<EuiFormRow
									label="Postal code"
									fullWidth
									isInvalid={shipToValidation.postalCodeMissing}
									error={shipToValidation.postalCodeMissing ? 'Required' : undefined}
								>
									<EuiFieldText
										fullWidth
										isInvalid={shipToValidation.postalCodeMissing}
										value={shipTo.postalCode}
										onChange={( e ) => updateShipTo( { postalCode: e.target.value } )}
									/>
								</EuiFormRow>
							</EuiFlexItem>
							<EuiFlexItem style={{ minWidth: 140 }}>
								<EuiFormRow label="Country" fullWidth>
									<EuiFieldText
										fullWidth
										disabled
										value={shipTo.country}
									/>
								</EuiFormRow>
							</EuiFlexItem>
						</EuiFlexGroup>
						<EuiSpacer size="m" />
						<EuiFlexGroup gutterSize="m" wrap responsive={false}>
							<EuiFlexItem grow={false} style={{ minWidth: 280, maxWidth: 280 }}>
								<PhoneInput
									label={<>Contact phone <span className="required">*</span></>}
									value={shipTo.contactPhone}
									onChange={( value ) => updateShipTo( { contactPhone: value } )}
									isInvalid={contactPhoneValidation.isInvalid}
									error={contactPhoneValidation.error}
								/>
							</EuiFlexItem>
						</EuiFlexGroup>
					</>
				)}

				<EuiHorizontalRule />

				<EuiTitle size="xs"><h3>Items</h3></EuiTitle>
				{missingItemMasterCount > 0 && (
					<>
						<EuiSpacer size="s" />
						<EuiCallOut
							color="danger"
							size="s"
							iconType="alert"
							title={`${missingItemMasterCount} ${missingItemMasterCount === 1 ? 'item does' : 'items do'} not have an inventory item master at ${fulfillingInventory.name}. Choose a substitute that has one, or remove the ${missingItemMasterCount === 1 ? 'item' : 'items'}, to create this fulfillment order.`}
						/>
					</>
				)}
				<EuiSpacer size="s" />
				<DataGrid
					rows={items}
					columns={itemColumns}
					getRowId={( row ) => row.lineItem.lineItemId}
					// Rows hold a two-line cell, a number input and a combobox that can
					// grow a warning box, so they have to size to their content.
					getRowHeight={() => 'auto'}
					getRowClassName={( { row } ) => (
						itemMasterStatuses.get( row.lineItem.lineItemId )?.isPresent ? '' : MISSING_ITEM_MASTER_ROW_CLASS
					)}
					emptyStateText="No items on this order"
					sx={{
						// EUI top-aligned every cell in this table; the pickers and inputs
						// would otherwise float against taller neighbours.
						'& .MuiDataGrid-cell': { alignItems: 'flex-start', whiteSpace: 'normal', py: 1 },
						[ `& .${MISSING_ITEM_MASTER_ROW_CLASS}` ]: { bgcolor: 'formPanel.errorBackground' },
						// SubstitutePartPicker's menu is portalled, but its closed input
						// must not be clipped by the cell.
						'& .MuiDataGrid-cell[data-field="substitute"]': { overflow: 'visible' },
					}}
				/>
			</EuiModalBody>
			<EuiModalFooter>
				<EuiButtonEmpty onClick={handleClose} disabled={isSubmitting}>
					Cancel
				</EuiButtonEmpty>
				<EuiButton
					fill
					onClick={handleSubmit}
					isLoading={isSubmitting}
					disabled={!isValid || isSubmitting}
				>
					Create fulfillment order
				</EuiButton>
			</EuiModalFooter>
			<NotesModal
				isOpen={!!notesModalLineItemId}
				onClose={() => setNotesModalLineItemId( null )}
				notes={notesModalRow?.fulfillerNotes ?? ''}
				onSave={handleSaveFulfillerNote}
				title={notesModalRow?.fulfillerNotes ? 'Edit Fulfillment Note' : 'Add Fulfillment Note'}
				placeholder="Enter your note to the requester here..."
			/>
		</EuiModal>
	)
}
